import React, { useState, useContext, useEffect, useReducer } from 'react';
import { useBlocker } from 'react-router-dom';

interface UseRouteGuardApi {
  isRouterBlocked: boolean;
  nextLocation: { [key: string]: any; pathname: string } | null;
  isRouteGuardModalOpen: boolean;
  blockRouter: () => void;
  unblockRouter: () => void;
  closeModal: () => void;
  handleContinueEditing: () => void;
  handleLeaveWithoutSaving: () => void;
}

interface State {
  isRouteGuardModalOpen: boolean;
  isRouterBlocked: boolean;
  listeners: any[];
  nextLocation: { [key: string]: any; pathname: string } | null;
  routeAction: string;
}

interface RouteGuardContextType {
  isRouterBlocked: boolean;
  nextLocation: { [key: string]: any; pathname: string } | null;
  isRouteGuardModalOpen: boolean;
  subscribe: (listener: (action: ROUTE_GUARD_ACTION) => any) => any;
  block: () => void;
  unblock: () => void;
  closeModal: () => void;
  handleContinueEditing: () => void;
  handleLeaveWithoutSaving: () => void;
}

export enum ROUTE_GUARD_ACTION {
  LEAVE_WITHOUT_SAVING = 'LEAVE_WITHOUT_SAVING',
  SAVE_AND_LEAVE = 'SAVE_AND_LEAVE',
  CONTINUE_EDITING = 'CONTINUE_EDITING',
  RESET_TO_GUARANTEE_ONE_EVENT_PROPOGATION = 'RESET_TO_GUARANTEE_ONE_EVENT_PROPOGATION',
}
/**
 * The RouteGuardProvider should be used as a RouteGuard that prevents user from leaving page and gives user options to resolve staying/leaving page
 */
const RouteGuardContext = React.createContext<RouteGuardContextType>({
  isRouterBlocked: false,
  nextLocation: null,
  isRouteGuardModalOpen: false,
  subscribe: () => null,
  block: () => null,
  unblock: () => null,
  closeModal: () => null,
  handleContinueEditing: () => null,
  handleLeaveWithoutSaving: () => null,
});

const getInitState = (): State => ({
  isRouteGuardModalOpen: false,
  isRouterBlocked: false,
  listeners: [],
  nextLocation: null,
  routeAction: '',
});

// PROVIDER
function RouteGuardProvider(props) {
  const [state, setState] = useState(getInitState());

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    setState({
      ...state,
      nextLocation,
    });
    return state.isRouterBlocked && currentLocation.pathname !== nextLocation.pathname;
  });

  const handleLeaveWithoutSaving = () => {
    for (const fn of state.listeners) {
      fn({ type: ROUTE_GUARD_ACTION.LEAVE_WITHOUT_SAVING });
    }
    setState({
      ...state,
      isRouteGuardModalOpen: false,
      routeAction: '',
      nextLocation: null,
      isRouterBlocked: false,
    });
    blocker?.proceed?.();
  };

  const handleContinueEditing = () => {
    for (const fn of state.listeners) {
      fn({ type: ROUTE_GUARD_ACTION.CONTINUE_EDITING });
    }

    setState({
      ...state,
      isRouteGuardModalOpen: false,
      routeAction: '',
      nextLocation: null,
      isRouterBlocked: true,
    });
  };

  const subscribe = (listener) => {
    if (state.listeners?.length) return;
    setState({
      ...state,
      listeners: [...state.listeners, listener],
    });
  };

  const block = () => {
    return setState({ ...state, isRouterBlocked: true });
  };

  const unblock = () => {
    return setState({
      ...state,
      isRouteGuardModalOpen: false,
      isRouterBlocked: false,
    });
  };

  const closeModal = () => {
    return setState({ ...state, isRouteGuardModalOpen: false });
  };

  const contextApi = {
    isRouterBlocked: state.isRouterBlocked,
    nextLocation: state.nextLocation,
    isRouteGuardModalOpen: state.isRouteGuardModalOpen,
    subscribe,
    block,
    unblock,
    closeModal,
    handleLeaveWithoutSaving,
    handleContinueEditing,
  };

  useEffect(() => {
    return () => {
      blocker.proceed?.();
      blocker.reset?.();
    };
  }, []);

  useEffect(() => {
    if (!state.isRouterBlocked) {
      setState({
        ...getInitState(),
        listeners: state.listeners,
      }); // not blocked -> clear state
      return;
    }
    if (!state.nextLocation) return;
    setState({ ...state, isRouteGuardModalOpen: true });
  }, [state.isRouterBlocked, state.nextLocation]);

  return (
    <RouteGuardContext.Provider value={contextApi}>{props.children}</RouteGuardContext.Provider>
  );
}

// HOOK
function useRouteGuard(): UseRouteGuardApi {
  const reducer = (state, action) => {
    switch (action.type) {
      case ROUTE_GUARD_ACTION.LEAVE_WITHOUT_SAVING:
        return {
          type: ROUTE_GUARD_ACTION.LEAVE_WITHOUT_SAVING,
        };
      case ROUTE_GUARD_ACTION.CONTINUE_EDITING:
        return {
          type: ROUTE_GUARD_ACTION.CONTINUE_EDITING,
        };
      case ROUTE_GUARD_ACTION.SAVE_AND_LEAVE:
        return { type: ROUTE_GUARD_ACTION.SAVE_AND_LEAVE };

      case ROUTE_GUARD_ACTION.RESET_TO_GUARANTEE_ONE_EVENT_PROPOGATION:
      default:
        return null;
    }
  };

  const [state, dispatch] = useReducer(reducer, null);

  const {
    isRouterBlocked,
    isRouteGuardModalOpen,
    nextLocation,
    block,
    unblock,
    subscribe,
    closeModal,
    handleLeaveWithoutSaving,
    handleContinueEditing,
  } = useContext(RouteGuardContext);

  if (state) {
    dispatch({ type: ROUTE_GUARD_ACTION.RESET_TO_GUARANTEE_ONE_EVENT_PROPOGATION });
  }

  useEffect(() => {
    subscribe((action) => {
      dispatch(action);
    });
  }, [isRouterBlocked]);

  return {
    isRouterBlocked,
    isRouteGuardModalOpen,
    nextLocation,
    handleLeaveWithoutSaving,
    handleContinueEditing,
    blockRouter: block,
    unblockRouter: unblock,
    closeModal,
  };
}

export { RouteGuardProvider, useRouteGuard };
